'use client';

import * as Sentry from '@sentry/nextjs';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import useCheckPermissions from 'hooks/useCheckPermissions';
import { usePersistentState } from '~/hooks';
import { ClubAdminAction } from '../club-ki-assistant/types';
import {
    APP_SUPPORT_CAPABILITIES,
    APP_SUPPORT_EXAMPLE_QUESTIONS,
    APP_SUPPORT_FEEDBACK_URL,
    APP_SUPPORT_CHAT_URL,
    AppSupportAiSettings,
    buildAppSupportAiSettingsUrl,
    buildAppSupportApproveUrl,
    buildAppSupportRejectUrl
} from './constants';
import {
    AppSupportActionStatus,
    AppSupportChatResponse,
    AppSupportHistoryItem,
    AppSupportMessage
} from './types';
import { normalizeAppSupportChatResponse } from './utils';

/** Build a chat message with a fresh id, merging any extra fields on top. */
const createMessage = (
    role: AppSupportMessage['role'],
    text: string,
    extra: Partial<AppSupportMessage> = {}
): AppSupportMessage => ({ id: uuidv4(), role, text, ...extra });

/**
 * Statuses that mean the request never reached the application: API Gateway
 * answered on its own behalf, so no PHP ran and nothing was persisted. Retrying
 * them cannot produce a duplicate Usage row or a duplicate staged action.
 *
 * 500 is deliberately not in this list. A gateway-level 500 carries no CORS
 * headers, so the browser blocks it and it arrives here as a network error, never
 * as a readable status — which means a 500 that does reach this code came from
 * the application itself. Retrying that spends two more Bedrock turns on a
 * failure that will repeat, and can duplicate the Usage row when the exception
 * happened after it was written.
 */
const APP_SUPPORT_RETRYABLE_STATUSES = [502, 503, 504];

/** Backoff between retries; its length also caps how many retries are attempted. */
const APP_SUPPORT_RETRY_DELAYS_MS = [2000, 5000];

/**
 * Carries the HTTP status of a failed request so the handler can tell an expired
 * session from an overloaded gateway. Previously every non-2xx collapsed into a
 * single "connection unavailable" message, which hid the actual cause.
 */
class AppSupportRequestError extends Error {
    readonly status: number;
    readonly backendMessage: string | null;
    readonly requestId: string | null;

    constructor(status: number, backendMessage: string | null, requestId: string | null) {
        super(`App support chat failed with HTTP ${status}`);
        this.name           = 'AppSupportRequestError';
        this.status         = status;
        this.backendMessage = backendMessage;
        this.requestId      = requestId;
    }
}

/** Marks a 2xx response whose body was missing or not valid JSON. */
class AppSupportResponseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AppSupportResponseError';
    }
}

/**
 * Elapsed time from which a failed request is read as the API Gateway cut-off
 * rather than a real network fault. The integration timeout is 29s (vapor.yml
 * sets the function to 30s), so anything at or beyond this mark ran to the
 * ceiling instead of failing to connect.
 */
const APP_SUPPORT_GATEWAY_TIMEOUT_MS = 25000;

/**
 * fetch() rejected without ever producing a Response.
 *
 * On Vapor this is what a killed Lambda (timeout, OOM, fatal) looks like from
 * the browser: API Gateway answers on its own behalf with a bodyless 5xx that
 * carries no CORS headers, the browser refuses to expose it, and JS is left with
 * an opaque `TypeError: Failed to fetch` plus a CORS message in the console.
 * No PHP ran, so there is no server log and no Sentry event either — the elapsed
 * time captured here is the only signal that separates the two causes: ~29s
 * means the gateway cut the request off, a few hundred ms means the connection
 * never got anywhere.
 */
class AppSupportNetworkError extends Error {
    readonly elapsedMs: number;
    readonly sourceError: unknown;

    constructor(sourceError: unknown, elapsedMs: number) {
        const source = sourceError instanceof Error ? `${sourceError.name}: ${sourceError.message}` : String(sourceError);
        super(`Keine Antwort vom Server nach ${(elapsedMs / 1000).toFixed(1)}s (${source})`);
        this.name        = 'AppSupportNetworkError';
        this.elapsedMs   = elapsedMs;
        this.sourceError = sourceError;
    }

    /** True when the request ran into the gateway ceiling instead of failing to connect. */
    get looksLikeGatewayTimeout(): boolean {
        return this.elapsedMs >= APP_SUPPORT_GATEWAY_TIMEOUT_MS;
    }
}

/** Pause used for the retry backoff. */
const wait = (ms: number) => new Promise<void>((resolve) => { window.setTimeout(resolve, ms); });

/**
 * Read the human-readable message out of an error response. The backend answers
 * failures the same way the other AI endpoints do — `{ error }` for the 401,
 * `{ message }` for an aborted request — and that text was being discarded.
 */
const readErrorMessage = async (res: Response): Promise<string | null> => {
    try {
        const body = (await res.json()) as { answer?: string; error?: string; message?: string };

        return body?.answer?.trim() || body?.error?.trim() || body?.message?.trim() || null;
    } catch {
        return null;
    }
};

/** Technical detail appended to every failure message so the cause is visible in the chat. */
const describeDetail = (error: unknown): string => {
    if (error instanceof AppSupportRequestError) {
        const parts = [`HTTP ${error.status}`];
        if (error.backendMessage) parts.push(error.backendMessage);
        if (error.requestId) parts.push(`Request-ID ${error.requestId}`);
        return parts.join(' · ');
    }
    if (error instanceof AppSupportNetworkError) {
        return `${error.name}: ${error.message}`;
    }
    if (error instanceof Error) {
        return `${error.name}: ${error.message}`;
    }
    return String(error);
};

/** Pick the message shown in the chat — the failure kind decides, and the raw error is appended. */
const describeFailure = (error: unknown): string => {
    const detail = describeDetail(error);

    if (error instanceof DOMException && error.name === 'AbortError') {
        return `Die Anfrage hat zu lange gedauert (${detail}). Bitte versuche es erneut oder formuliere die Frage etwas kürzer.`;
    }

    if (error instanceof AppSupportRequestError) {
        if (error.status === 401 || error.status === 403) {
            return `${error.backendMessage || 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu und melde dich erneut an.'} (${detail})`;
        }

        if (error.status === 429) {
            return `Gerade sind zu viele Anfragen unterwegs (${detail}). Bitte versuche es in einem Moment noch einmal.`;
        }

        if (APP_SUPPORT_RETRYABLE_STATUSES.includes(error.status)) {
            return `Der Assistent hat mit einem Serverfehler geantwortet (${detail}).`;
        }

        return `Die Anfrage konnte nicht verarbeitet werden (${detail}).`;
    }

    if (error instanceof AppSupportResponseError) {
        return `Die Antwort des Assistenten war nicht lesbar (${detail}).`;
    }

    if (error instanceof AppSupportNetworkError) {
        return error.looksLikeGatewayTimeout
            ? `Der Server hat die Anfrage nach ${(error.elapsedMs / 1000).toFixed(1)}s abgebrochen, bevor eine Antwort fertig war (${detail}). `
              + 'Bitte stelle die Frage etwas kürzer oder versuche es erneut.'
            : `Netzwerkfehler, keine Antwort vom Server (${detail}). Bitte prüfe deine Verbindung und versuche es erneut.`;
    }

    // fetch() itself rejected: no HTTP status exists. On Vapor this is a Lambda that
    // died before Laravel answered (no CORS headers) or the API Gateway cut-off.
    return `Netzwerkfehler, keine Antwort vom Server (${detail}). Auf Vapor: CloudWatch-Log der Lambda prüfen.`;
};

/**
 * Print the failure to the browser console.
 *
 * The chat bubble gets one sentence for the user; this is the version for
 * whoever has DevTools open. It exists because the worst failure of this
 * endpoint is invisible everywhere else: when the Lambda is killed, the browser
 * prints only a CORS message, no PHP ran, and Sentry's server-side project never
 * sees an event. `hint` names that case explicitly so the next person does not
 * start by debugging the CORS configuration, which is not the cause.
 */
const logFailure = (error: unknown, context: Record<string, unknown>) => {
    const isGatewayCutoff = error instanceof AppSupportNetworkError && error.looksLikeGatewayTimeout;

    const hint = isGatewayCutoff
        ? 'Die Antwort kam nicht aus PHP (deshalb fehlen die CORS-Header). Die Lambda wurde nach ~30s beendet, '
          + 'bevor Laravel antworten konnte — CloudWatch-Log der Vapor-Funktion prüfen, Sentry sieht diesen Fall nicht.'
        : error instanceof AppSupportNetworkError
          ? 'fetch() hat ohne Response abgebrochen: Verbindung, DNS oder eine blockierte Antwort. Kein Serverfehler nachweisbar.'
          : error instanceof DOMException && error.name === 'AbortError'
            ? 'Der Client hat abgebrochen (eigenes Zeitlimit oder Navigation), nicht der Server.'
            : error instanceof AppSupportRequestError
              ? 'Die Antwort kam aus der Anwendung — Status und Request-ID sind auswertbar.'
              : 'Die Antwort kam an, war aber leer oder kein gültiges JSON.';

    console.error('[app-support-chat] Anfrage fehlgeschlagen', {
        error,
        url: APP_SUPPORT_CHAT_URL,
        detail: describeDetail(error),
        elapsedMs: error instanceof AppSupportNetworkError ? Math.round(error.elapsedMs) : null,
        status: error instanceof AppSupportRequestError ? error.status : null,
        requestId: error instanceof AppSupportRequestError ? error.requestId : null,
        ...context,
        hint
    });
};

/**
 * Report the failure to Sentry. The gateway request id is the link to CloudWatch
 * for the failures that never reach PHP and therefore leave no server-side trace
 * at all; it is readable thanks to `exposed_headers` in the backend CORS config.
 */
const captureFailure = (error: unknown, context: Record<string, string | null>) => {
    // A user-cancelled or timed-out request is not a defect worth reporting.
    if (error instanceof DOMException && error.name === 'AbortError') return;

    Sentry.captureException(error, {
        tags: {
            feature: 'app_support_chat',
            app_support_status: error instanceof AppSupportRequestError ? String(error.status) : 'network',
            app_support_request_id: error instanceof AppSupportRequestError ? error.requestId : null,
            // Separates a killed Lambda from a genuine network fault; both arrive
            // as an opaque fetch rejection and would otherwise group as one issue.
            app_support_failure:
                error instanceof AppSupportNetworkError
                    ? (error.looksLikeGatewayTimeout ? 'gateway_timeout' : 'network')
                    : 'application'
        },
        extra: {
            ...context,
            elapsed_ms: error instanceof AppSupportNetworkError ? Math.round(error.elapsedMs) : null
        }
    });
};

/** Build the auth headers for the raw fetch calls this hook makes outside the axios middleware. */
const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${Cookies.get('token') || ''}`,
    'X-Requested-With': 'XMLHttpRequest'
});

/** Resolve the route the user is on, so the backend can prefer matching modules. */
const getCurrentRoute = (pathname?: string, asPath?: string) => {
    if (pathname) return pathname;
    if (asPath) return asPath.split('?')[0]?.split('#')[0] || undefined;
    if (typeof window !== 'undefined') return window.location.pathname;
    return undefined;
};

const APP_SUPPORT_REQUEST_TIMEOUT_MS = 120000;
const INITIAL_MESSAGES: AppSupportMessage[] = [];

/** State and transport for the app support chat: transcript, AI consent, and staged actions. */
export const useAppSupportChatbot = (clubSlug: string) => {
    const router = useRouter();
    const api = useApi();
    const { t } = useTranslation('ai');
    const [isMounted, setIsMounted] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState<string | null>(null);
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
    const conversationIdRef = useRef<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const storageKey = useMemo(() => `app_support_chatbot_messages_${clubSlug}`, [clubSlug]);
    const [messages, setMessages] = usePersistentState<AppSupportMessage[]>(storageKey, INITIAL_MESSAGES);
    const { hasAccess: isClubAdmin } = useCheckPermissions({ clubSlug, permission: 'club.settings' });

    // Club-level consent for the admin (Vereinsdaten) tools. Stored on the club
    // and toggled only by a super admin (Vereinsleitung) — no more localStorage.
    const [aiEnabled, setAiEnabled] = useState(false);
    const [canManageAi, setCanManageAi] = useState(false);
    const [aiSettingsLoaded, setAiSettingsLoaded] = useState(false);
    const [aiSettingsSaving, setAiSettingsSaving] = useState(false);

    useEffect(() => { setIsMounted(true); }, []);

    /** Mirror the club's AI consent payload into local state. */
    const applyAiSettings = (data: AppSupportAiSettings) => {
        setAiEnabled(Boolean(data?.enabled));
        setCanManageAi(Boolean(data?.can_manage));
    };

    // Load the club's AI consent — only club admins may read it.
    useEffect(() => {
        if (!clubSlug || !isClubAdmin) {
            setAiSettingsLoaded(true);
            return;
        }

        let cancelled = false;
        setAiSettingsLoaded(false);

        void api({
            url: buildAppSupportAiSettingsUrl(clubSlug),
            method: 'GET',
            muted: true,
            onSuccess: (data: AppSupportAiSettings) => {
                if (!cancelled) applyAiSettings(data);
            },
            onError: () => {
                if (!cancelled) {
                    setAiEnabled(false);
                    setCanManageAi(false);
                }
            },
            onFinish: () => {
                if (!cancelled) setAiSettingsLoaded(true);
            }
        });

        return () => { cancelled = true; };
        // `api` is recreated each render; only re-run when the club/permission changes.
    }, [clubSlug, isClubAdmin]);

    /** Persist the club-wide AI consent; only a super admin may change it. */
    const setAiAccess = (enabled: boolean) => {
        if (!clubSlug || !canManageAi || aiSettingsSaving) return;
        setAiSettingsSaving(true);
        void api({
            url: buildAppSupportAiSettingsUrl(clubSlug),
            method: 'PATCH',
            data: { enabled },
            onSuccess: (data: AppSupportAiSettings) => applyAiSettings(data),
            onFinish: () => setAiSettingsSaving(false)
        });
    };

    /** Turn the club's AI data features on. */
    const enableAi = () => setAiAccess(true);

    /** Turn the club's AI data features off. */
    const disableAi = () => setAiAccess(false);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            listRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [messages]);

    useEffect(() => {
        if (loading) return;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
        return () => window.clearTimeout(timer);
    }, [loading]);

    const exampleQuestions = [...APP_SUPPORT_EXAMPLE_QUESTIONS];
    const capabilities = useMemo(
        () => APP_SUPPORT_CAPABILITIES.filter((capability) => !capability.admin || isClubAdmin),
        [isClubAdmin]
    );
    const hasMessages = (messages || []).length > 0;

    /** Put a suggested prompt into the input (without sending) and focus it for editing. */
    const prefillInput = (value: string) => {
        setInput(value);
        window.setTimeout(() => inputRef.current?.focus(), 0);
    };

    /** Lazily create the conversation id shared by every turn of this chat. */
    const ensureConversationId = () => {
        if (conversationIdRef.current === null) conversationIdRef.current = uuidv4();
        return conversationIdRef.current;
    };

    /** Pair user questions with their answers into the history shape the backend expects. */
    const buildHistoryPayload = (items: AppSupportMessage[]): AppSupportHistoryItem[] => {
        const payload: AppSupportHistoryItem[] = [];
        for (let i = 0; i < items.length - 1; i++) {
            if (items[i].role === 'user' && items[i + 1]?.role === 'model') {
                payload.push({ userQuestion: items[i].text, AIResponse: items[i + 1].text });
            }
        }
        return payload;
    };

    /** Clear the transcript and start a fresh conversation id. */
    const resetConversation = () => {
        setMessages([]);
        setInput('');
        setLoading(false);
        setStatusText(null);
        conversationIdRef.current = uuidv4();
    };

    /** Run a single request and append the model message. */
    const runAttempt = async (payload: string) => {
        const controller = new AbortController();
        const timeoutId  = window.setTimeout(() => controller.abort(), APP_SUPPORT_REQUEST_TIMEOUT_MS);
        const startedAt  = performance.now();

        try {
            let res: Response;
            try {
                res = await fetch(APP_SUPPORT_CHAT_URL, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    signal: controller.signal,
                    body: payload
                });
            } catch (error) {
                // A client-side abort passes through unchanged; anything else is the
                // opaque "Failed to fetch" that hides why no response arrived.
                if (error instanceof DOMException && error.name === 'AbortError') throw error;
                throw new AppSupportNetworkError(error, performance.now() - startedAt);
            }

            if (!res.ok) {
                throw new AppSupportRequestError(
                    res.status,
                    await readErrorMessage(res),
                    res.headers.get('apigw-requestid')
                );
            }

            let data: AppSupportChatResponse;
            try {
                data = (await res.json()) as AppSupportChatResponse;
            } catch {
                throw new AppSupportResponseError('App support chat returned a body that is not valid JSON.');
            }

            const normalized = normalizeAppSupportChatResponse(
                data,
                'Ich habe dazu gerade keine Antwort erhalten.',
                clubSlug
            );

            setMessages((current) => [
                ...((current || []) as AppSupportMessage[]),
                createMessage('model', normalized.text, {
                    actions: normalized.actions,
                    routeTitle: normalized.routeTitle,
                    suggestedRoute: normalized.suggestedRoute,
                    usageId: data.usage_id ?? null,
                    views: normalized.views
                })
            ]);
            setStatusText(null);
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    /** Send the question, retrying the statuses that mean nothing ran server-side. */
    const ask = async (overrideInput?: string, staticPrompt = false) => {
        const question = (overrideInput || input).trim();
        if (!question || !clubSlug || loading) return;

        setLoading(true);
        setInput('');
        setStatusText(null);

        const userMessage = createMessage('user', question);
        const nextMessages = [...(messages || []), userMessage];
        setMessages(nextMessages);

        const conversationId = ensureConversationId();
        const currentRoute   = getCurrentRoute(router.pathname, router.asPath);
        const payload        = JSON.stringify({
            prompt: question,
            history: buildHistoryPayload(nextMessages),
            conversation_id: conversationId,
            current_route: currentRoute,
            static_prompt: staticPrompt,
            // The backend gates admin tools on the club-level AI setting +
            // this user's permission; the RAG chat works either way.
            club_slug: clubSlug
        });

        try {
            for (let attempt = 0; ; attempt++) {
                try {
                    await runAttempt(payload);
                    return;
                } catch (error) {
                    const retryable =
                        error instanceof AppSupportRequestError
                        && APP_SUPPORT_RETRYABLE_STATUSES.includes(error.status);

                    if (!retryable || attempt >= APP_SUPPORT_RETRY_DELAYS_MS.length) throw error;

                    setStatusText(t('appSupportChat.loading.retry'));
                    await wait(APP_SUPPORT_RETRY_DELAYS_MS[attempt]);
                }
            }
        } catch (error) {
            const context = {
                conversation_id: conversationId,
                current_route: currentRoute ?? null,
                club_slug: clubSlug
            };
            logFailure(error, context);
            captureFailure(error, context);
            setMessages((current) => [
                ...((current || []) as AppSupportMessage[]),
                createMessage('model', describeFailure(error))
            ]);
            setStatusText(null);
        } finally {
            setLoading(false);
        }
    };

    /** Record the approve/reject outcome of a staged action on its message. */
    const setActionStatus = (messageId: string, actionId: number, status: AppSupportActionStatus) => {
        setMessages((current) =>
            (current || []).map((m) =>
                m.id === messageId ? { ...m, actionStatus: { ...(m.actionStatus || {}), [actionId]: status } } : m
            )
        );
    };

    /** Append a plain assistant message without any actions or views. */
    const appendModelMessage = (text: string) =>
        setMessages((current) => [...((current || []) as AppSupportMessage[]), createMessage('model', text)]);

    /** Approve or reject a staged admin action and report the outcome in the chat. */
    const reviewAction = (messageId: string, action: ClubAdminAction, decision: 'approve' | 'reject') => {
        if (!clubSlug || action.action_id === null || actionLoadingId !== null) return;

        const actionId = action.action_id;
        setActionLoadingId(actionId);

        void api({
            url:
                decision === 'approve'
                    ? buildAppSupportApproveUrl(clubSlug, actionId)
                    : buildAppSupportRejectUrl(clubSlug, actionId),
            method: 'PATCH',
            muted: true,
            onSuccess: (data: { message?: string }) => {
                setActionStatus(messageId, actionId, decision === 'approve' ? 'approved' : 'rejected');
                appendModelMessage(
                    data?.message ||
                        (decision === 'approve'
                            ? 'Die Aktion wurde genehmigt und erfolgreich ausgeführt.'
                            : 'Die Aktion wurde abgelehnt.')
                );
            },
            onError: (error: { response?: { data?: { error?: string } } }) => {
                const backendMessage = error?.response?.data?.error;
                appendModelMessage(
                    backendMessage
                        ? `Die Aktion konnte nicht verarbeitet werden: ${backendMessage}`
                        : 'Die Aktion konnte gerade nicht verarbeitet werden. Bitte versuche es erneut oder nutze die Freigabe-Seite.'
                );
            },
            onFinish: () => setActionLoadingId(null)
        });
    };

    /** Approve a staged admin action. */
    const approveAction = (messageId: string, action: ClubAdminAction) => reviewAction(messageId, action, 'approve');

    /** Reject a staged admin action. */
    const rejectAction = (messageId: string, action: ClubAdminAction) => reviewAction(messageId, action, 'reject');

    /** Store a thumbs up/down for an answer, optimistically in the UI first. */
    const sendFeedback = (messageId: string, usageId: number | null | undefined, rating: 1 | -1) => {
        setMessages((current) =>
            (current || []).map((m) =>
                m.id === messageId ? { ...m, feedback: rating === 1 ? 'up' : 'down' } : m
            )
        );

        if (!usageId) return;

        // Best-effort — errors are swallowed via muted.
        void api({
            url: APP_SUPPORT_FEEDBACK_URL,
            method: 'POST',
            data: { usage_id: usageId, rating },
            muted: true
        });
    };

    /** Send on Enter, keep Shift+Enter free for a line break. */
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void ask();
        }
    };

    return {
        actionLoadingId,
        aiEnabled,
        aiSettingsLoaded,
        aiSettingsSaving,
        approveAction,
        ask,
        canManageAi,
        capabilities,
        disableAi,
        enableAi,
        exampleQuestions,
        hasMessages,
        input,
        inputRef,
        isClubAdmin,
        isMounted,
        listRef,
        loading,
        messages,
        onKeyDown,
        prefillInput,
        rejectAction,
        resetConversation,
        sendFeedback,
        setInput,
        statusText
    };
};
